"use client";

import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

export default function CTASection() {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  return (
    <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden bg-deep-dark" ref={ref}>
      {/* Animated background gradient */}
      <motion.div 
        className="absolute inset-0 pointer-events-none" 
        style={{
          background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(91, 46, 255, 0.2) 0%, rgba(124, 77, 255, 0.1) 50%, transparent 100%)"
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.8, 1, 0.8]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
        <motion.span 
          className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full glass-card border border-accent-primary/20 text-accent-primary text-xs font-bold tracking-widest uppercase font-heading mb-6 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-glow-pulse" />
          Start in 48 hours
        </motion.span>

        <motion.h2
          className="font-heading font-extrabold text-text-primary mb-4 sm:mb-6 px-4"
          style={{ fontSize: "clamp(28px, 6vw, 60px)", letterSpacing: "-0.025em", lineHeight: "1.1" }}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Ready to scale without{" "}
          <span className="gradient-text">logistics chaos?</span>
        </motion.h2>

        <motion.p 
          className="text-base sm:text-lg text-text-secondary max-w-xl mx-auto mb-8 sm:mb-10 leading-relaxed px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Book a free 30-minute strategy call. We'll audit your current logistics setup and show you exactly how Helium can cut your costs and delivery times.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link
            href="/contact"
            className="group relative inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl text-white font-bold overflow-hidden transition-all duration-500 hover:scale-105 w-full sm:w-auto min-h-[52px] sm:min-h-[56px]"
            style={{
              fontSize: "clamp(14px, 2vw, 16px)",
              background: "linear-gradient(135deg, #5B2EFF 0%, #7C4DFF 50%, #9F7AEA 100%)",
              boxShadow: "0 0 50px rgba(91, 46, 255, 0.4), 0 8px 24px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Icon name="PhoneIcon" size={18} className="relative z-10" />
            <span className="relative z-10">Book a Free Call</span>
            {/* Micro shimmer animation */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 3s linear infinite",
              }}
            />
          </Link>
          <Link
            href="/pricing"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl border-2 border-glass-border bg-glass-white backdrop-blur-xl text-text-primary font-bold transition-all duration-300 hover:bg-opacity-10 hover:border-accent-primary w-full sm:w-auto min-h-[52px] sm:min-h-[56px]"
            style={{
              fontSize: "clamp(14px, 2vw, 16px)",
            }}
          >
            View Pricing
            <Icon name="ArrowRightIcon" size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}